import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { MessageCircle } from 'lucide-react';
import {
  Accordion,
  AccordionItem,
  AccordionTrigger,
  AccordionContent,
} from '@/components/ui/accordion';
import { useSettings } from '@/contexts/SettingsContext';
import { listOps, newBlockId, useEditablePage } from '@/hooks/useEditablePage';
import EditableText from '@/components/pageEditor/EditableText';
import RichText, { RichHtml } from '@/components/pageEditor/RichText';
import IconPicker from '@/components/pageEditor/IconPicker';
import EditBar from '@/components/pageEditor/EditBar';
import PageState from '@/components/pageEditor/PageState';
import { AddBlockButton, BlockControls } from '@/components/pageEditor/BlockControls';

const newQuestion = () => ({ id: newBlockId(), q: 'سؤال جديد؟', a: '<p></p>' });

/** مجموعة أسئلة: أكورديون للزوار، وقائمة قابلة للتعديل للأدمن */
const FaqGroup = ({ group, index, count, editing, update, groupOps }) => {
  const path = ['content', 'groups', index];
  const items = group.items ?? [];
  const itemOps = listOps(update, [...path, 'items']);

  return (
    <section className="relative bg-white dark:bg-gray-900/40 rounded-lg shadow-sm border border-gray-100 dark:border-white/10 p-6 md:p-8">
      {editing && (
        <BlockControls
          className="absolute -top-4 left-4 z-10"
          index={index}
          count={count}
          onMove={groupOps.move}
          onRemove={groupOps.remove}
          label="المجموعة"
        />
      )}
      <h2 className="flex items-center gap-3 text-xl md:text-2xl font-tajawal font-bold mb-2">
        <IconPicker value={group.icon} editing={editing} onChange={(v) => update([...path, 'icon'], v)}>
          {(Icon) => <Icon className="w-6 h-6 text-burgundy-500 flex-shrink-0" />}
        </IconPicker>
        <EditableText
          editing={editing}
          value={group.title}
          onChange={(v) => update([...path, 'title'], v)}
          placeholder="عنوان المجموعة"
          className="flex-1"
        />
      </h2>

      {editing ? (
        <div className="space-y-4 mt-4">
          {items.map((item, i) => (
            <div key={item.id} className="rounded-lg border border-gray-100 dark:border-white/10 p-4 space-y-2">
              <div className="flex items-start gap-3">
                <EditableText
                  editing
                  value={item.q}
                  onChange={(v) => update([...path, 'items', i, 'q'], v)}
                  placeholder="السؤال"
                  className="flex-1 font-cairo font-semibold"
                />
                <BlockControls index={i} count={items.length} onMove={itemOps.move} onRemove={itemOps.remove} label="السؤال" />
              </div>
              <RichText
                editing
                html={item.a}
                onChange={(v) => update([...path, 'items', i, 'a'], v)}
                placeholder="الإجابة..."
                className="font-cairo text-base text-gray-700 dark:text-gray-300 leading-relaxed"
              />
            </div>
          ))}
          <AddBlockButton label="إضافة سؤال" onClick={() => itemOps.insert(items.length, newQuestion())} />
        </div>
      ) : (
        <Accordion type="single" collapsible className="w-full">
          {items.map((item) => (
            <AccordionItem
              key={item.id}
              value={item.id}
              className="border-gray-100 dark:border-white/10 last:border-b-0"
            >
              <AccordionTrigger className="font-cairo font-semibold text-base text-right hover:no-underline hover:text-burgundy-500 gap-4">
                {item.q}
              </AccordionTrigger>
              <AccordionContent className="font-cairo text-base text-gray-700 dark:text-gray-300 leading-relaxed">
                <RichHtml html={item.a} />
              </AccordionContent>
            </AccordionItem>
          ))}
        </Accordion>
      )}
    </section>
  );
};

const FAQ = () => {
  const site = useSettings();
  const page = useEditablePage('faq');
  const { data, editing, update } = page;

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  if (!data) return <PageState page={page} />;

  const content = data.content ?? {};
  const groups = content.groups ?? [];
  const groupOps = listOps(update, ['content', 'groups']);

  return (
    <div className="py-12">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8 max-w-[900px]">
        <div className="text-center mb-12">
          {(editing || data.icon) && (
            <div className="w-16 h-16 bg-burgundy-500/10 rounded-full flex items-center justify-center mx-auto mb-5">
              <IconPicker value={data.icon} editing={editing} onChange={(v) => update(['icon'], v)}>
                {(Icon) => <Icon className="w-8 h-8 text-burgundy-500" />}
              </IconPicker>
            </div>
          )}
          <EditableText
            as="h1"
            editing={editing}
            value={data.title}
            onChange={(v) => update(['title'], v)}
            placeholder="عنوان الصفحة"
            className="text-4xl md:text-5xl font-tajawal font-bold mb-4"
          />
          <EditableText
            as="p"
            editing={editing}
            multiline
            value={data.subtitle}
            onChange={(v) => update(['subtitle'], v)}
            placeholder="وصف قصير تحت العنوان (اختياري)"
            className="font-cairo text-gray-600 dark:text-gray-400 leading-relaxed max-w-2xl mx-auto"
          />
        </div>

        <div className="space-y-6">
          {groups.map((group, index) => (
            <FaqGroup
              key={group.id}
              group={group}
              index={index}
              count={groups.length}
              editing={editing}
              update={update}
              groupOps={groupOps}
            />
          ))}
          {editing && (
            <AddBlockButton
              label="إضافة مجموعة أسئلة"
              onClick={() =>
                groupOps.insert(groups.length, { id: newBlockId(), icon: 'HelpCircle', title: 'مجموعة جديدة', items: [newQuestion()] })
              }
            />
          )}
        </div>

        <div className="mt-10 bg-burgundy-500/5 border border-burgundy-500/20 rounded-lg p-8 text-center">
          <MessageCircle className="w-10 h-10 text-burgundy-500 mx-auto mb-4" />
          <EditableText
            as="h2"
            editing={editing}
            value={content.cta_title}
            onChange={(v) => update(['content', 'cta_title'], v)}
            placeholder="عنوان صندوق التواصل"
            className="block text-2xl font-tajawal font-bold mb-3"
          />
          <EditableText
            as="p"
            editing={editing}
            multiline
            value={content.cta_text}
            onChange={(v) => update(['content', 'cta_text'], v)}
            placeholder="نص صندوق التواصل"
            className="block font-cairo text-gray-600 dark:text-gray-400 mb-6"
          />
          <div className="flex flex-wrap justify-center gap-4">
            <a
              href={site.whatsapp_url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 bg-green-500 hover:bg-green-600 text-white font-cairo font-semibold px-6 py-3 rounded-lg transition-colors duration-200"
            >
              <MessageCircle className="w-5 h-5" />
              راسلينا على واتساب
            </a>
            <Link
              to="/contact"
              className="inline-flex items-center gap-2 bg-burgundy-500 hover:bg-burgundy-600 text-white font-cairo font-semibold px-6 py-3 rounded-lg transition-colors duration-200"
            >
              صفحة التواصل
            </Link>
          </div>
        </div>
      </div>
      <EditBar page={page} />
    </div>
  );
};

export default FAQ;
